﻿/**
 * AuthProvider - Authentication Context Provider
 *
 * Provides authentication state and actions to the entire application.
 * This provider uses Firebase Authentication with the adapter pattern
 * to support multiple authentication methods.
 *
 * Migration Note (2026-02-22):
 * Updated to use Firebase Authentication per Architecture Guardrail 1-006.
 * MSAL/Entra ID direct integration has been removed.
 *
 * Features:
 * - Automatic initialization and session restoration via Firebase
 * - Provider-agnostic authentication interface
 * - Error handling and loading states
 * - Firebase onAuthStateChanged integration
 */

import {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  type ReactNode,
} from 'react';
import { onAuthStateChanged, type User } from 'firebase/auth';
import { auth, getUserClaims } from './firebase';
import { AuthContext, type AuthContextValue } from './AuthContext';
import {
  type AuthUser,
  type AuthProviderType,
  type LoginOptions,
  providerRegistry,
  firebaseMicrosoftAdapter,
  firebaseGoogleAdapter,
  firebaseEmailAdapter,
} from './adapters';

/**
 * Props for the AuthProvider component
 */
interface AuthProviderProps {
  /** Child components that will have access to auth context */
  children: ReactNode;

  /** Initial provider type to use (defaults to 'firebase-microsoft') */
  initialProvider?: AuthProviderType;
}

/**
 * Internal state for the auth provider
 */
interface InternalAuthState {
  isLoading: boolean;
  isAuthenticated: boolean;
  user: AuthUser | null;
  roles: string[];
  error: Error | null;
  providerType: AuthProviderType | null;
}

/**
 * Convert Firebase User to AuthUser
 */
async function firebaseUserToAuthUser(user: User): Promise<AuthUser> {
  const idToken = await user.getIdToken();

  return {
    id: user.uid,
    email: user.email || '',
    displayName: user.displayName || user.email || 'User',
    photoUrl: user.photoURL || undefined,
    idToken,
    accessToken: idToken,
    tokenExpiry: undefined,
  };
}

/**
 * AuthProvider Component
 *
 * Wraps the application to provide authentication context.
 * Uses Firebase onAuthStateChanged for reactive auth state management.
 */
export function AuthProvider({
  children,
  initialProvider = 'firebase-microsoft',
}: AuthProviderProps) {
  // Internal state
  const [state, setState] = useState<InternalAuthState>({
    isLoading: true,
    isAuthenticated: false,
    user: null,
    roles: [],
    error: null,
    providerType: null,
  });

  // Track when a login is in progress to prevent race conditions
  // During login, onAuthStateChanged should not set isAuthenticated
  // because we need the API call to complete first
  const loginInProgressRef = useRef(false);

  /**
   * Initialize the authentication system
   */
  useEffect(() => {
    let mounted = true;

    // Register the Firebase Microsoft adapter (primary provider)
    providerRegistry.register(firebaseMicrosoftAdapter);

    // Register the Firebase Google adapter
    providerRegistry.register(firebaseGoogleAdapter);
    
    // Register the Firebase Email adapter (alternative provider)
    providerRegistry.register(firebaseEmailAdapter);

    // Set the active provider from environment or props
    const envProvider = import.meta.env.VITE_AUTH_PROVIDER as AuthProviderType | undefined;
    const activeProvider = envProvider || initialProvider;

    try {
      providerRegistry.setActive(activeProvider);
    } catch {
      // If the requested provider isn't available, use firebase-microsoft
      console.warn(
        `Provider '${activeProvider}' not available, using firebase-microsoft`
      );
      providerRegistry.setActive('firebase-microsoft');
    }

    // Subscribe to Firebase auth state changes
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!mounted) return;

      // If a login is in progress, let the login() function handle state
      // This prevents premature isAuthenticated=true before API call completes
      if (loginInProgressRef.current) {
        console.info('[AuthProvider] Login in progress, skipping onAuthStateChanged');
        return;
      }

      if (user) {
        try {
          // Convert Firebase user to AuthUser
          const authUser = await firebaseUserToAuthUser(user);

          // Get roles from custom claims
          const claims = await getUserClaims(user);
          const roles = claims.role ? [claims.role] : [];

          setState({
            isLoading: false,
            isAuthenticated: true,
            user: authUser,
            roles,
            error: null,
            providerType: providerRegistry.getActiveProviderType(),
          });
        } catch (error) {
          console.error('[AuthProvider] Error processing user:', error);
          setState((prev) => ({
            ...prev,
            isLoading: false,
            error: error instanceof Error ? error : new Error('Failed to load user'),
          }));
        }
      } else {
        // No user - not authenticated
        setState({
          isLoading: false,
          isAuthenticated: false,
          user: null,
          roles: [],
          error: null,
          providerType: providerRegistry.getActiveProviderType(),
        });
      }
    });

    return () => {
      mounted = false;
      unsubscribe();
    };
  }, [initialProvider]);

  /**
   * Login action
   */
  const login = useCallback(async (options?: LoginOptions) => {
    const adapter = providerRegistry.getActive();
    if (!adapter) {
      throw new Error('No authentication provider configured');
    }

    // Mark login in progress to prevent onAuthStateChanged race conditions
    loginInProgressRef.current = true;
    setState((prev) => ({ ...prev, isLoading: true, error: null }));

    try {
      await adapter.login(options);

      // Refresh user state after login
      const user = await adapter.getUser();
      const roles = await adapter.getRoles();

      setState({
        isLoading: false,
        isAuthenticated: true,
        user,
        roles,
        error: null,
        providerType: adapter.providerType,
      });
    } catch (error) {
      console.error('[AuthProvider] Login error:', error);
      setState({
        isLoading: false,
        isAuthenticated: false,
        user: null,
        roles: [],
        error: error instanceof Error ? error : new Error('Login failed'),
        providerType: adapter.providerType,
      });
      throw error;
    } finally {
      // Always clear the login-in-progress flag
      loginInProgressRef.current = false;
    }
  }, []);

  /**
   * Register action (email/password only)
   */
  const register = useCallback(async (email: string, password: string) => {
    // Registration only works with email provider
    const emailAdapter = providerRegistry.get('firebase-email');
    if (!emailAdapter || !('register' in emailAdapter)) {
      throw new Error('Email registration is not available');
    }

    // Mark login in progress to prevent onAuthStateChanged race conditions
    loginInProgressRef.current = true;
    setState((prev) => ({ ...prev, isLoading: true, error: null }));

    try {
      // Set email as active provider and register
      providerRegistry.setActive('firebase-email');
      await (emailAdapter as { register: (email: string, password: string) => Promise<void> }).register(email, password);

      // Refresh user state after registration
      const user = await emailAdapter.getUser();
      const roles = await emailAdapter.getRoles();

      setState({
        isLoading: false,
        isAuthenticated: true,
        user,
        roles,
        error: null,
        providerType: 'firebase-email',
      });
    } catch (error) {
      console.error('[AuthProvider] Register error:', error);
      setState({
        isLoading: false,
        isAuthenticated: false,
        user: null,
        roles: [],
        error: error instanceof Error ? error : new Error('Registration failed'),
        providerType: 'firebase-email',
      });
      throw error;
    } finally {
      loginInProgressRef.current = false;
    }
  }, []);

  /**
   * Logout action
   */
  const logout = useCallback(async () => {
    const adapter = providerRegistry.getActive();
    if (!adapter) {
      return;
    }

    setState((prev) => ({ ...prev, isLoading: true, error: null }));

    try {
      await adapter.logout();

      setState((prev) => ({
        ...prev,
        isLoading: false,
        isAuthenticated: false,
        user: null,
        roles: [],
        error: null,
      }));
    } catch (error) {
      console.error('[AuthProvider] Logout error:', error);
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: error instanceof Error ? error : new Error('Logout failed'),
      }));
      throw error;
    }
  }, []);

  /**
   * Refresh user data
   */
  const refreshUser = useCallback(async () => {
    const adapter = providerRegistry.getActive();
    if (!adapter || !adapter.isAuthenticated()) {
      return;
    }

    try {
      const user = await adapter.getUser();
      const roles = await adapter.getRoles();

      setState((prev) => ({
        ...prev,
        user,
        roles,
      }));
    } catch (error) {
      console.error('[AuthProvider] Refresh user error:', error);
    }
  }, []);

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setState((prev) => ({ ...prev, error: null }));
  }, []);

  /**
   * Switch authentication provider
   */
  const switchProvider = useCallback((providerType: AuthProviderType) => {
    try {
      providerRegistry.setActive(providerType);
      setState((prev) => ({
        ...prev,
        providerType,
        // Reset auth state when switching providers
        isAuthenticated: false,
        user: null,
        roles: [],
      }));
    } catch (error) {
      console.error('[AuthProvider] Switch provider error:', error);
      setState((prev) => ({
        ...prev,
        error:
          error instanceof Error
            ? error
            : new Error('Failed to switch provider'),
      }));
    }
  }, []);

  /**
   * Memoized context value
   */
  const contextValue = useMemo<AuthContextValue>(
    () => ({
      isLoading: state.isLoading,
      isAuthenticated: state.isAuthenticated,
      user: state.user,
      roles: state.roles,
      error: state.error,
      providerType: state.providerType,
      login,
      register,
      logout,
      refreshUser,
      clearError,
      switchProvider,
    }),
    [
      state.isLoading,
      state.isAuthenticated,
      state.user,
      state.roles,
      state.error,
      state.providerType,
      login,
      register,
      logout,
      refreshUser,
      clearError,
      switchProvider,
    ]
  );

  // Provide auth context to children
  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
}
